import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Descriptions,
  Divider,
  Modal,
  Popover,
  Space,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  FilePdfOutlined,
  SwapOutlined,
  CopyOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getQuotationById,
  deleteQuotation,
  sendQuotation,
  convertToOrder,
  createRevision,
  getQuotationPDF,
} from "../../api/quotationApi";
import QuotationStatusBadge from "./QuotationStatusBadge";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Title, Text } = Typography;

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteVisible, setDeleteVisible] = useState(false);

  // Fetch quotation
  useEffect(() => {
    const fetchQuotation = async () => {
      setLoading(true);
      try {
        const data = await getQuotationById(id);
        setQuotation(data);
      } catch (error) {
        toast.error("Failed to load quotation");
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotation();
  }, [id]);

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  // Handle actions
  const handleDelete = async () => {
    try {
      await deleteQuotation(id);
      toast.warning("Quotation deleted");
      setDeleteVisible(false);
      navigate("/quotation");
    } catch (error) {
      toast.error("Failed to delete quotation");
    }
  };

  const handleSend = async () => {
    try {
      const result = await sendQuotation(id);
      setQuotation(result);
      toast.success("Quotation sent successfully");
    } catch (error) {
      toast.error("Failed to send quotation");
    }
  };

  const handleConvert = () => {
    Modal.confirm({
      title: "Convert to Order",
      content: "Convert this quotation to a confirmed order?",
      okText: "Convert",
      onOk: async () => {
        try {
          await convertToOrder(id);
          toast.success("Order created successfully");
          // Refresh quotation data (status should now be 'converted')
          const data = await getQuotationById(id);
          setQuotation(data);
        } catch (error) {
          toast.error("Failed to convert quotation");
        }
      },
    });
  };

  const handleRevision = async () => {
    try {
      const result = await createRevision(id);
      toast.success("Revision created");
      navigate(`/quotation/${result.id}/edit`);
    } catch (error) {
      toast.error("Failed to create revision");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await getQuotationPDF(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${quotation?.quotation_number || "quotation"}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  // Item columns
  const itemColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Box Type",
      dataIndex: "subcategory",
      key: "subcategory",
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_, record) =>
        `${record.length} × ${record.width} × ${record.height} ${record.unit_type || ""}`,
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      render: (ply) => `${ply} Ply`,
      align: "center",
    },
    {
      title: "GSM",
      dataIndex: "net_gsm",
      key: "net_gsm",
      align: "center",
    },
    {
      title: "Sheet Size",
      key: "sheet_size",
      render: (_, record) =>
        record.roll_size && record.cutting_size
          ? `${record.roll_size}" × ${record.cutting_size}"`
          : "-",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
    },
    {
      title: "Rate/Unit",
      dataIndex: "rate_per_unit",
      key: "rate_per_unit",
      render: (rate) => `₹${Number(rate || 0).toFixed(2)}`,
      align: "right",
    },
    {
      title: "Total",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) =>
        `₹${Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}`,
      align: "right",
    },
  ];

  if (loading) {
    return (
      <div className="text-center" style={{ padding: 60 }}>
        <Spin size="large" tip="Loading quotation..." />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Title level={4}>Quotation not found</Title>
        <Button onClick={() => navigate("/quotation")}>
          Back to Quotations
        </Button>
      </div>
    );
  }

  const isDraft = quotation.status === "draft";
  const isAccepted = quotation.status === "accepted";
  const canDelete = ["draft", "rejected", "expired"].includes(
    quotation.status
  );
  const canRevise = ["sent", "viewed", "rejected"].includes(
    quotation.status
  );

  return (
    <>
      <PageTitle title="Back" />
      <div className="mr-top">
        <Card bordered={false}>
          {/* Header */}
          <div className="card-header d-flex justify-content-between m-3">
            <div>
              <h5>
                <i className="bi bi-file-earmark-text"></i>
                <span className="mr-left">
                  Quotation: {quotation.quotation_number}
                </span>
              </h5>
              <QuotationStatusBadge status={quotation.status} />
              {quotation.version > 1 && (
                <span style={{ marginLeft: 8, color: "#888" }}>
                  Version {quotation.version}
                </span>
              )}
            </div>
            <div className="text-end">
              <Space wrap>
                {isDraft && (
                  <Link to={`/quotation/${quotation.id}/edit`}>
                    <Button
                      type="primary"
                      shape="round"
                      icon={<EditOutlined />}
                    >
                      Edit
                    </Button>
                  </Link>
                )}
                {isDraft && (
                  <Button
                    shape="round"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                  >
                    Send
                  </Button>
                )}
                {canRevise && (
                  <Button
                    shape="round"
                    icon={<CopyOutlined />}
                    onClick={handleRevision}
                  >
                    Revise
                  </Button>
                )}
                {isAccepted && (
                  <Button
                    type="primary"
                    shape="round"
                    icon={<SwapOutlined />}
                    onClick={handleConvert}
                  >
                    Convert to Order
                  </Button>
                )}
                <Button
                  shape="round"
                  icon={<FilePdfOutlined />}
                  onClick={handleDownloadPDF}
                >
                  PDF
                </Button>
                {canDelete && (
                  <Popover
                    content={
                      <a onClick={handleDelete}>
                        <Button type="primary" danger>
                          Yes Please !
                        </Button>
                      </a>
                    }
                    title="Are you sure you want to delete?"
                    trigger="click"
                    open={deleteVisible}
                    onOpenChange={setDeleteVisible}
                  >
                    <Button
                      type="danger"
                      shape="round"
                      icon={<DeleteOutlined />}
                    />
                  </Popover>
                )}
              </Space>
            </div>
          </div>

          <Divider />

          {/* Quotation Info */}
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Quotation #">
              {quotation.quotation_number}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {quotation.customer_name || quotation.customer?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <QuotationStatusBadge status={quotation.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {moment(quotation.created_at).format("DD MMM YYYY, hh:mm A")}
            </Descriptions.Item>
            <Descriptions.Item label="Valid Until">
              {quotation.valid_until
                ? moment(quotation.valid_until).format("DD MMM YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Validity">
              {quotation.validity_days} days
            </Descriptions.Item>
            {quotation.sent_at && (
              <Descriptions.Item label="Sent At">
                {moment(quotation.sent_at).format("DD MMM YYYY, hh:mm A")}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Version">
              v{quotation.version || 1}
            </Descriptions.Item>
          </Descriptions>

          {/* Line Items Table */}
          <Title level={5} style={{ marginBottom: 16 }}>
            Line Items
          </Title>
          <Table
            columns={itemColumns}
            dataSource={quotation.items || []}
            rowKey="id"
            pagination={false}
            scroll={{ x: 1000 }}
            bordered
            size="small"
          />

          {/* Totals */}
          <Row justify="end" style={{ marginTop: 24 }}>
            <Col xs={24} md={8}>
              <div
                style={{
                  background: "#fafafa",
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <Row justify="space-between" style={{ marginBottom: 8 }}>
                  <span>Subtotal:</span>
                  <strong>
                    ₹
                    {Number(quotation.subtotal || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </strong>
                </Row>
                {(quotation.discount_percent || 0) > 0 && (
                  <Row justify="space-between" style={{ marginBottom: 8 }}>
                    <span>Discount ({quotation.discount_percent}%):</span>
                    <span style={{ color: "#f5222d" }}>
                      - ₹
                      {Number(quotation.discount_amount || 0).toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 }
                      )}
                    </span>
                  </Row>
                )}
                <Row justify="space-between" style={{ marginBottom: 8 }}>
                  <span>Tax ({quotation.tax_rate || 0}%):</span>
                  <span>
                    ₹
                    {Number(quotation.tax_amount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </Row>
                <Divider style={{ margin: "8px 0" }} />
                <Row
                  justify="space-between"
                  style={{ fontSize: 18, fontWeight: "bold" }}
                >
                  <span>Total:</span>
                  <span>
                    ₹
                    {Number(quotation.total_amount || 0).toLocaleString(
                      "en-IN",
                      { minimumFractionDigits: 2 }
                    )}
                  </span>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Notes & Terms */}
          {(quotation.notes ||
            quotation.terms_conditions ||
            quotation.internal_notes) && (
            <>
              <Divider />
              <Row gutter={16}>
                {quotation.notes && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Notes</Title>
                    <p>{quotation.notes}</p>
                  </Col>
                )}
                {quotation.terms_conditions && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Terms & Conditions</Title>
                    <p>{quotation.terms_conditions}</p>
                  </Col>
                )}
                {quotation.internal_notes && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Internal Notes</Title>
                    <p style={{ color: "#888" }}>
                      {quotation.internal_notes}
                    </p>
                  </Col>
                )}
              </Row>
            </>
          )}

          {/* Revisions */}
          {quotation.revisions && quotation.revisions.length > 0 && (
            <>
              <Divider />
              <Title level={5}>Revision History</Title>
              {quotation.revisions.map((rev) => (
                <div key={rev.id} style={{ marginBottom: 8 }}>
                  <Link to={`/quotation/${rev.id}`}>
                    {rev.quotation_number} (v{rev.version})
                  </Link>
                  <span style={{ marginLeft: 8, color: "#888" }}>
                    — {moment(rev.created_at).format("DD MMM YYYY")}
                  </span>
                  <QuotationStatusBadge status={rev.status} />
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
    </>
  );
};

export default QuotationDetail;
